import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Building2, HardHat, HeartHandshake, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import logo from "@/assets/ffs-logo-primary.svg";
import BrandLogo from "@/components/BrandLogo";
import { useAppName } from "@/lib/branding";

const CRIMSON = "var(--brand)";
const GREEN = "#00c86e";

export default function Login() {
  const appName = useAppName();
  const { login, verifyOtp, choosePersona, twofaConfirm } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("password"); // password | otp | persona | twofa-setup | forgot
  const [challenge, setChallenge] = useState(null); // otp_required / twofa_setup_required response
  const [dual, setDual] = useState(null); // dual_role response { full_name, auth_ticket }
  const [forgotEmail, setForgotEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [setupMethod, setSetupMethod] = useState(null); // chosen 2FA method
  const [setupInfo, setSetupInfo] = useState(null);     // { secret, otpauth_uri, sent_to }
  const [setupCode, setSetupCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining
  const resendTimer = useRef(null);
  const [trustDevice, setTrustDevice] = useState(false); // checkbox for "Trust this device"
  const [trustDeviceSetup, setTrustDeviceSetup] = useState(false); // checkbox for 2FA setup

  const startResendCooldown = (secs = 30) => {
    setResendCooldown(secs);
    clearInterval(resendTimer.current);
    resendTimer.current = setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) { clearInterval(resendTimer.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  // Sent here by an expired session (lib/session.js) — say why, once.
  const [expired] = useState(() => new URLSearchParams(window.location.search).get("expired") === "1");
  useEffect(() => {
    if (expired) window.history.replaceState(null, "", window.location.pathname);
  }, [expired]);

  const routeTo = (u) => nav(u.role === "Support Worker" ? "/worker" : "/");
  const next = (res) => {
    if (res.password_setup_required) { setForgotEmail(username); setSent(true); setStep("forgot"); toast.success(res.message || "Check your email to set your password."); return; }
    if (res.twofa_setup_required) { setChallenge(res); setStep("twofa-setup"); setSetupMethod(null); setSetupInfo(null); setSetupCode(""); return; }
    if (res.otp_required) { setChallenge(res); setStep("otp"); setOtp(""); startResendCooldown(30); return; }
    if (res.dual_role) { setDual(res); setStep("persona"); return; }
    routeTo(res);
  };
  const reset = () => { setStep("password"); setChallenge(null); setDual(null); setOtp(""); setPassword(""); setSent(false); setSetupMethod(null); setSetupInfo(null); setSetupCode(""); };

  const chooseMethod = async (m) => {
    setLoading(true);
    try { const r = await api.twofaChoose({ challenge_id: challenge.challenge_id, method: m }); setSetupMethod(m); setSetupInfo(r); setSetupCode(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't start setup. Please try again."); }
    finally { setLoading(false); }
  };
  const confirmSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const r = await twofaConfirm({ challenge_id: challenge.challenge_id, code: setupCode.trim(), trust_device: trustDeviceSetup }); if (r.role) routeTo(r); else next(r); }
    catch (err) { toast.error(err.response?.data?.detail || "That code didn't work. Please try again."); }
    finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.forgotPassword(forgotEmail.trim());
      setSent(true);
      toast.success(r.message || "If an account with that email exists, a reset link is on its way.");
    } catch {
      // Endpoint returns generic success; treat any error as sent to avoid enumeration.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      next(await login(username.trim(), password));
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't sign you in. Please check your email and password and try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      next(await verifyOtp({ username: username.trim(), password, otp: otp.trim(), challenge_id: challenge.challenge_id, trust_device: trustDevice }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "That verification code didn't work. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      const r = await api.resendOtp({ challenge_id: challenge.challenge_id, username: username.trim() });
      toast.success(r.prompt || "A new code has been sent.");
      startResendCooldown(30);
      setOtp("");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      // If the server says wait N seconds, parse it and start a local countdown
      const match = detail.match(/wait (\d+) more second/);
      if (match) startResendCooldown(parseInt(match[1], 10));
      toast.error(detail || "Couldn't resend the code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const choose = async (persona) => {
    setLoading(true);
    try {
      routeTo(await choosePersona({ auth_ticket: dual.auth_ticket, persona }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign in with that role.");
      reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-body" style={{ backgroundColor: "#fcfcfc" }}>
      {/* Brand hero */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-14 relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, #f2fbf6 0%, #e7f7ef 55%, #dff3ea 100%)" }}>
        {/* soft brand blobs */}
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full blur-3xl opacity-50" style={{ backgroundColor: "#a8eacc" }} />
        <div className="absolute bottom-[-6rem] right-[-4rem] h-96 w-96 rounded-full blur-3xl opacity-30" style={{ backgroundColor: "var(--brand)" }} />

        <div className="relative">
          <div className="inline-flex rounded-2xl bg-white px-6 py-5 shadow-sm ring-1 ring-black/5">
            <BrandLogo fallback={logo} className="h-20 w-auto" data-testid="login-logo" />
          </div>
        </div>

        <div className="relative">
          <h1 className="font-brand font-bold tracking-tight text-slate-900 leading-[1.1]" style={{ fontSize: "3rem" }}>
            Welcome to<br /><span style={{ color: CRIMSON }}>{appName}</span>
          </h1>
          <p className="mt-5 text-slate-600 text-base max-w-md leading-relaxed">
            The home for your supports, shifts and everything that keeps great care running smoothly. Sign in to pick up where you left off.
          </p>
          <div className="mt-7 flex flex-col gap-3 text-sm text-slate-700 max-w-md">
            <div className="flex items-start gap-2.5">
              <HeartHandshake className="h-5 w-5 shrink-0 mt-0.5" style={{ color: GREEN }} />
              <span>For our <span className="font-semibold">support workers and office team</span> — your rosters, timesheets and notes, all in one place.</span>
            </div>
            <div className="flex items-start gap-2.5">
              <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" style={{ color: GREEN }} />
              <span><span className="font-semibold">Participant portal is now live</span> — a place to see your supports and stay connected.</span>
            </div>
          </div>
        </div>

        <div className="relative text-xs text-slate-500">Australia-hosted · Private &amp; secure sign-in</div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex justify-center mb-8">
            <BrandLogo fallback={logo} className="h-14 w-auto" />
          </div>

          {step === "persona" && dual ? (
            <div data-testid="persona-chooser">
              <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight">Hello again{dual.full_name ? `, ${dual.full_name.split(" ")[0]}` : ""} 👋</h2>
              <p className="text-sm text-slate-500 mt-1.5">You have both office and support-worker access. How are you signing in today?</p>
              <div className="mt-6 space-y-3">
                <button type="button" onClick={() => choose("office")} disabled={loading} data-testid="persona-office"
                  className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-brand hover:bg-brand/5 transition-colors disabled:opacity-60">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: CRIMSON }}><Building2 className="h-5 w-5" /></div>
                  <div><div className="text-sm font-semibold text-slate-900">Office team</div><div className="text-xs text-slate-500">Rosters, timesheets &amp; billing</div></div>
                </button>
                <button type="button" onClick={() => choose("worker")} disabled={loading} data-testid="persona-worker"
                  className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-[#00c86e] hover:bg-emerald-50/40 transition-colors disabled:opacity-60">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: "#3f9963" }}><HardHat className="h-5 w-5" /></div>
                  <div><div className="text-sm font-semibold text-slate-900">Support worker</div><div className="text-xs text-slate-500">My shifts, clock in/out &amp; notes</div></div>
                </button>
              </div>
              <button type="button" onClick={reset} className="mt-5 text-xs text-slate-400 hover:text-slate-600" data-testid="persona-back">
                ← Use a different account
              </button>
              {loading && <div className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Signing in…</div>}
            </div>
          ) : step === "twofa-setup" && challenge ? (
            <div data-testid="twofa-setup">
              <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" style={{ color: GREEN }} /> Set up 2-step sign-in
              </h2>
              {!setupMethod ? (
                <>
                  <p className="text-sm text-slate-500 mt-1.5">For your account's security, choose how you'd like to verify it's you at sign-in.</p>
                  <div className="mt-6 space-y-3">
                    {(challenge.methods || []).includes("totp") && (
                      <button type="button" onClick={() => chooseMethod("totp")} disabled={loading} data-testid="twofa-method-totp"
                        className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-[#00c86e] transition-colors disabled:opacity-60">
                        <div className="text-sm font-semibold text-slate-900">Authenticator app (recommended)</div>
                        <div className="text-xs text-slate-500">Use Google Authenticator, Authy, etc.</div>
                      </button>
                    )}
                    {(challenge.methods || []).includes("email") && (
                      <button type="button" onClick={() => chooseMethod("email")} disabled={loading} data-testid="twofa-method-email"
                        className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-brand transition-colors disabled:opacity-60">
                        <div className="text-sm font-semibold text-slate-900">Email a code</div>
                        <div className="text-xs text-slate-500">Sent to {challenge.email_masked}</div>
                      </button>
                    )}
                    {(challenge.methods || []).includes("sms") && (
                      <button type="button" onClick={() => chooseMethod("sms")} disabled={loading} data-testid="twofa-method-sms"
                        className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-brand transition-colors disabled:opacity-60">
                        <div className="text-sm font-semibold text-slate-900">Text me a code</div>
                        <div className="text-xs text-slate-500">Sent to {challenge.mobile_masked}</div>
                      </button>
                    )}
                  </div>
                  <button type="button" onClick={reset} className="mt-5 text-xs text-slate-400 hover:text-slate-600" data-testid="twofa-setup-back">← Use a different account</button>
                </>
              ) : (
                <form onSubmit={confirmSetup} className="space-y-4 mt-3">
                  {setupMethod === "totp" && setupInfo?.secret && (
                    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="twofa-totp-secret">
                      <p className="text-[11px] text-slate-500">Add this key to your authenticator app, then enter the 6-digit code it shows:</p>
                      <code className="mt-1.5 block text-center text-sm font-semibold tracking-widest text-slate-800 break-all">{setupInfo.secret}</code>
                    </div>
                  )}
                  {setupMethod !== "totp" && (
                    <p className="text-sm text-slate-500">We sent a 6-digit code to <span className="font-medium text-slate-700">{setupInfo?.sent_to}</span>. Enter it below.</p>
                  )}
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Verification code</Label>
                    <Input data-testid="twofa-setup-code" inputMode="numeric" autoComplete="one-time-code" autoFocus
                      value={setupCode} onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="123456" className="mt-1 h-11 text-sm tracking-widest" required />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer group" data-testid="trust-device-setup-checkbox">
                      <input
                        type="checkbox"
                        checked={trustDeviceSetup}
                        onChange={(e) => setTrustDeviceSetup(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-xs text-slate-700 group-hover:text-slate-900">Trust this device for 30 days</span>
                    </label>
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 flex items-start gap-2">
                      <span className="text-amber-600 text-sm shrink-0">⚠️</span>
                      <p className="text-[11px] text-amber-700 leading-relaxed">Don't enable this on shared or public devices.</p>
                    </div>
                  </div>
                  <Button data-testid="twofa-setup-confirm" type="submit" disabled={loading} className="w-full h-11 text-sm font-semibold rounded-xl hover:opacity-95" style={{ backgroundColor: CRIMSON }}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & finish"}
                  </Button>
                  <button type="button" onClick={() => setSetupMethod(null)} className="w-full text-xs text-slate-400 hover:text-slate-600" data-testid="twofa-setup-choose-again">← Choose a different method</button>
                </form>
              )}
            </div>
          ) : step === "otp" && challenge ? (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="otp-form">
              <div>
                <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" style={{ color: GREEN }} /> Verify it's you
                </h2>
                <p className="text-sm text-slate-500 mt-1.5">
                  {challenge.prompt || "Enter the 6-digit code from your authenticator app."}
                </p>
              </div>
              {challenge.qrcode && (
                <div className="rounded-xl border border-slate-200 bg-white p-3 text-center" data-testid="otp-setup-qr">
                  <p className="text-[11px] text-slate-500 mb-2">First time? Scan this with your authenticator app, then enter the code it shows.</p>
                  {challenge.qrcode.trim().startsWith("<") ? (
                    <div className="mx-auto h-40 w-40 [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: challenge.qrcode }} />
                  ) : (
                    <img src={challenge.qrcode} alt="Verification setup QR" className="mx-auto h-40 w-40 object-contain" />
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold text-slate-700">Verification code</Label>
                <Input data-testid="otp-input" inputMode="numeric" autoComplete="one-time-code" autoFocus
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  placeholder="123456" className="mt-1 h-11 text-sm tracking-widest" required />
              </div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer group" data-testid="trust-device-checkbox">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-xs text-slate-700 group-hover:text-slate-900">Trust this device for 30 days</span>
                </label>
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 flex items-start gap-2">
                  <span className="text-amber-600 text-sm shrink-0">⚠️</span>
                  <p className="text-[11px] text-amber-700 leading-relaxed">Don't enable this on shared or public devices.</p>
                </div>
              </div>
              <Button data-testid="otp-submit" type="submit" disabled={loading} className="w-full h-11 text-sm font-semibold rounded-xl hover:opacity-95" style={{ backgroundColor: CRIMSON }}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
              </Button>
              {challenge.method !== "totp" && (
                <div className="flex items-center justify-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={resendOtp}
                    disabled={loading || resendCooldown > 0}
                    className="text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: resendCooldown > 0 ? "#94a3b8" : CRIMSON }}
                    data-testid="otp-resend"
                  >
                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
                  </button>
                </div>
              )}
              <button type="button" onClick={reset} className="w-full text-xs text-slate-400 hover:text-slate-600" data-testid="otp-back">
                ← Use a different account
              </button>
            </form>
          ) : step === "forgot" ? (
            <div data-testid="forgot-form">
              {sent ? (
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4"><MailCheck className="h-6 w-6" style={{ color: GREEN }} /></div>
                  <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight">Check your email</h2>
                  <p className="text-sm text-slate-500 mt-2">If an account exists for <span className="font-medium text-slate-700">{forgotEmail.trim() || "that address"}</span>, we've sent a link to reset your password. It expires in 1 hour.</p>
                  <p className="text-xs text-slate-400 mt-3">Didn't get it? Check your spam folder, or try again in a few minutes.</p>
                  <button type="button" onClick={reset} className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="forgot-back">
                    <ArrowLeft className="h-4 w-4" /> Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={submitForgot} className="space-y-4">
                  <div>
                    <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight">Forgot your password?</h2>
                    <p className="text-sm text-slate-500 mt-1.5">Enter your email and we'll send you a link to set a new one.</p>
                  </div>
                  <div>
                    <Label htmlFor="forgot-email-input" className="text-xs font-semibold text-slate-700">Email address</Label>
                    <Input data-testid="forgot-email" id="forgot-email-input" name="email" type="email" autoComplete="username" inputMode="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@prydessupport.com" className="mt-1 h-11 text-sm" autoFocus required />
                  </div>
                  <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full h-11 text-sm font-semibold rounded-xl hover:opacity-95" style={{ backgroundColor: CRIMSON }}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                  </Button>
                  <button type="button" onClick={reset} className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-600" data-testid="forgot-cancel">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                  </button>
                </form>
              )}
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight">Sign in</h2>
              <p className="text-sm text-slate-500 mt-1.5">Welcome back — please sign in to continue.</p>
            </div>
            {expired && (
              <p data-testid="session-expired" role="status" className="text-sm rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2">
                Your session has expired. Please sign in again.
              </p>
            )}
            <div>
              <Label htmlFor="login-email" className="text-xs font-semibold text-slate-700">Email address</Label>
              <Input data-testid="login-username" id="login-email" name="email" type="email" autoComplete="username" inputMode="email"
                value={username} onChange={(e) => setUsername(e.target.value)} placeholder="you@prydessupport.com" className="mt-1 h-11 text-sm" required />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="login-password" className="text-xs font-semibold text-slate-700">Password</Label>
                <button type="button" onClick={() => { setForgotEmail(username); setSent(false); setStep("forgot"); }} className="text-xs font-medium hover:underline" style={{ color: CRIMSON }} data-testid="forgot-password-link">Forgot password?</button>
              </div>
              <Input data-testid="login-password" id="login-password" name="password" type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="mt-1 h-11 text-sm" required />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 text-sm font-semibold rounded-xl hover:opacity-95" style={{ backgroundColor: CRIMSON }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
            <p className="text-center text-xs text-slate-400 pt-1">Need help signing in? Contact your office.</p>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
